import { MediaMatcher } from '@angular/cdk/layout';
import {
    ChangeDetectorRef,
    Component,
    type OnDestroy,
    type Signal,
    signal,
    type WritableSignal,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    GuardsCheckEnd,
    GuardsCheckStart,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationStart,
    Router,
    type Event,
} from '@angular/router';
import { distinctUntilChanged, filter, map, of, switchMap, timer } from 'rxjs';
import { VERSION } from '@pecan/config';
import { HeaderMenuComponent } from '../header-menu/header-menu.component';
import { MatSidenavContainer, MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { MatIcon } from '@angular/material/icon';
import { MainMenuComponent } from '../main-menu/main-menu.component';
import { TranslatePipe } from '@ngx-translate/core';

type NavigationEvent = NavigationStart | GuardsCheckStart | GuardsCheckEnd | NavigationEnd | NavigationCancel | NavigationError;

const NAVIGATION_LOADER_THRESHOLD_MS = 500;

const isNavigationEvent = (e: Event): e is NavigationEvent =>
    e instanceof NavigationStart ||
    e instanceof GuardsCheckStart ||
    (e instanceof GuardsCheckEnd && e.shouldActivate) ||
    e instanceof NavigationEnd ||
    e instanceof NavigationCancel ||
    e instanceof NavigationError;

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [HeaderMenuComponent, MatSidenavContainer, MatSidenav, MatIcon, MainMenuComponent, MatSidenavContent, TranslatePipe],
    selector: 'app-module-layout',
    styleUrls: ['./module-layout.component.scss'],
    templateUrl: './module-layout.component.html',
})
export class ModuleLayoutComponent implements OnDestroy {
    public readonly mobileQuery!: MediaQueryList;

    public readonly version: string = VERSION;

    public readonly isNavigating: Signal<boolean>;

    private readonly _mobileQueryListener: () => void;

    private readonly _isNavigating: WritableSignal<boolean> = signal(false);

    private readonly _router: Router = inject(Router);

    constructor() {
        const changeDetectorRef = inject(ChangeDetectorRef);
        const media = inject(MediaMatcher);

        this.mobileQuery = media.matchMedia('(max-width: 1024px)');
        this._mobileQueryListener = (): void => changeDetectorRef.detectChanges();
        this.mobileQuery.addEventListener('change', this._mobileQueryListener);

        this.isNavigating = this._isNavigating.asReadonly();
        this._listenToRouteTransitions();
    }

    public ngOnDestroy(): void {
        this.mobileQuery.removeEventListener('change', this._mobileQueryListener);
    }

    public onLogoClick(): void {
        this._router.navigate(['home']);
    }

    private _listenToRouteTransitions(): void {
        this._router.events
            .pipe(
                filter(isNavigationEvent),
                switchMap((e: NavigationEvent) => {
                    if (e instanceof NavigationStart || e instanceof GuardsCheckEnd) {
                        return timer(NAVIGATION_LOADER_THRESHOLD_MS).pipe(map(() => true));
                    }

                    return of(false);
                }),
                distinctUntilChanged(),
                takeUntilDestroyed()
            )
            .subscribe((show: boolean) => this._isNavigating.set(show));
    }
}
